import React from "react";
import { PageHeader, Card, Money } from "../components/ui-bj/Primitives";
import { Loading, ErrorState } from "../components/ui-bj/States";
import { useAirtable } from "../hooks/useAirtable";
import { money, num, str, fmtDate } from "../lib/airtableHelpers";
import ExportButtons from "../components/ui-bj/ExportButtons";

const PodiumCard = ({ rank, entry }) => {
  const HEIGHT = rank === 1 ? "h-40" : rank === 2 ? "h-32" : "h-24";
  const COLOR = rank === 1 ? "#6E5022" : rank === 2 ? "#8A6A3A" : "#5E4D40";
  return (
    <div data-testid={`podium-rank-${rank}`} className="flex flex-col items-center gap-3 flex-1">
      <div className="text-center">
        <div className="bj-label">RANK 0{rank}</div>
        <div className="font-heading text-[#2A1C15] text-xl tracking-wide">{entry.owner}</div>
        <div className="text-[#6E5022] bj-tnum text-xs">{entry.code}</div>
      </div>
      <div className={`w-full ${HEIGHT} flex flex-col items-center justify-center border-t-2`} style={{ borderColor: COLOR, background: "#E7DDCB" }}>
        <div className="bj-metric">{entry.total}</div>
        <div className="bj-label">Referrals</div>
      </div>
    </div>
  );
};

const ReferralLeaderboardPage = () => {
  const refs = useAirtable("referrals");

  if (refs.loading) return <Loading label="Loading referrals" />;
  if (refs.error) return <ErrorState message={refs.error} onRetry={refs.refetch} />;

  const rows = refs.records
    .map((r) => {
      const f = r.fields || {};
      const total = num(f["Total Referrals"]) ?? 0;
      const perRef = money(f["Reward Per Referral"]);
      const earned = total * perRef;
      const paid = money(f["Rewards Paid"]);
      return {
        id: r.id,
        code: str(f["Referral Code"]),
        owner: str(f["Code Owner"]),
        type: str(f["Owner Type"]),
        total,
        earned,
        paid,
        pending: Math.max(earned - paid, 0),
        active: !!f["Code Active"],
        created: f["Date Created"],
      };
    })
    .sort((a, b) => b.total - a.total);

  const podium = rows.slice(0, 3);

  return (
    <div data-testid="referral-leaderboard-page">
      <PageHeader
        eyebrow="DASHBOARD / REFERRAL LEADERBOARD"
        title="Referral Leaderboard"
        subtitle="Top performing referrers and rewards balances across all codes."
        right={
          <ExportButtons
            title="Referral Leaderboard"
            filename="referrals"
            testidPrefix="export-referrals"
            columns={[
              { key: "code", label: "Code" },
              { key: "owner", label: "Owner" },
              { key: "type", label: "Type" },
              { key: "total", label: "Total Referrals" },
              { key: "earned", label: "Rewards Earned" },
              { key: "paid", label: "Rewards Paid" },
              { key: "pending", label: "Rewards Pending" },
              { key: "active", label: "Active", get: (r) => (r.active ? "Yes" : "No") },
              { key: "created", label: "Date Created" },
            ]}
            rows={rows}
          />
        }
      />

      {podium.length > 0 && (
        <Card className="mb-6">
          <div className="flex flex-col md:flex-row items-end gap-6 px-4 pb-4">
            {podium[1] && <PodiumCard rank={2} entry={podium[1]} />}
            {podium[0] && <PodiumCard rank={1} entry={podium[0]} />}
            {podium[2] && <PodiumCard rank={3} entry={podium[2]} />}
          </div>
        </Card>
      )}

      <Card className="overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="text-left">
              {["Code","Owner","Type","Referrals","Rewards Earned","Rewards Paid","Rewards Pending","Active","Created"].map((h) => (
                <th key={h} className="bj-table-header py-2 pr-3 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="py-10 text-center text-[#5E4D40] text-sm">No referral codes yet.</td></tr>}
            {rows.map((r, i) => (
              <tr key={r.id} data-testid={`referral-row-${i}`} className="bj-table-row">
                <td className="py-3 pr-3 text-[#6E5022] bj-tnum text-sm">{r.code}</td>
                <td className="py-3 pr-3 text-[#2A1C15] text-sm">{r.owner}</td>
                <td className="py-3 pr-3 text-[#5E4D40] text-xs uppercase tracking-widest">{r.type}</td>
                <td className="py-3 pr-3 font-heading text-[#6E5022] text-lg bj-tnum">{r.total}</td>
                <td className="py-3 pr-3 text-sm"><Money value={r.earned} tone="white" /></td>
                <td className="py-3 pr-3 text-sm"><Money value={r.paid} tone="green" /></td>
                <td className="py-3 pr-3 text-sm"><Money value={r.pending} tone="amber" /></td>
                <td className="py-3 pr-3 text-xs uppercase tracking-widest font-heading">
                  <span className={r.active ? "text-[#14281E]" : "text-[#5E4D40]"}>{r.active ? "Active" : "Inactive"}</span>
                </td>
                <td className="py-3 pr-3 text-[#5E4D40] bj-tnum text-sm">{fmtDate(r.created)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

export default ReferralLeaderboardPage;
